/**
 * InvoiceFormTotals
 *
 * Totals section: taxable amount, GST breakdown, round-off toggle, grand total.
 * Also renders the credit limit warning banner.
 */

import React from 'react'

import { formatAmount } from '../../../utils/billing-helpers'
import { Toggle } from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface InvoiceFormTotalsProps {
  form: UseInvoiceFormReturn
}

export function InvoiceFormTotals({ form }: InvoiceFormTotalsProps): JSX.Element {
  const { totals, taxBreakdown, supplyType, currency, isRoundOffEnabled, setIsRoundOffEnabled, creditLimitWarning } = form

  return (
    <>
      <div className="card el-p-md totals-section">
        <div className="totals-grid">
          {totals.discountAmount > 0 && (
            <>
              <div className="total-row">
                <span>Subtotal:</span>
                <span className="amount">{formatAmount(totals.subtotal, currency)}</span>
              </div>
              <div className="total-row">
                <span>Discount:</span>
                <span className="amount">-{formatAmount(totals.discountAmount, currency)}</span>
              </div>
            </>
          )}

          <div className="total-row">
            <span>Taxable Amount:</span>
            <span className="amount">{formatAmount(totals.taxableAmount, currency)}</span>
          </div>

          <TaxBreakdownRows currency={currency} supplyType={supplyType} taxBreakdown={taxBreakdown} />

          <RoundOffRow
            currency={currency}
            isRoundOffEnabled={isRoundOffEnabled}
            roundOffAmount={totals.roundOffAmount}
            onToggle={setIsRoundOffEnabled}
          />

          <div className="total-row total-final">
            <span>
              <strong>Total Amount:</strong>
            </span>
            <span className="amount">
              <strong>{formatAmount(totals.grandTotal, currency)}</strong>
            </span>
          </div>
        </div>
      </div>

      {creditLimitWarning && (
        <CreditLimitWarning message={creditLimitWarning} />
      )}
    </>
  )
}

// =============================================================================
// Sub-components
// =============================================================================

interface TaxBreakdownRowsProps {
  taxBreakdown: Array<[number, { cgst: number; sgst: number; igst: number }]>
  supplyType: string
  currency: string
}

function TaxBreakdownRows({ taxBreakdown, supplyType, currency }: TaxBreakdownRowsProps): JSX.Element {
  return (
    <>
      {taxBreakdown.map(([rate, amounts]) =>
        supplyType === 'intrastate' ? (
          <React.Fragment key={String(rate)}>
            <div className="total-row">
              <span>CGST {rate / 2}%:</span>
              <span className="amount">{formatAmount(amounts.cgst, currency)}</span>
            </div>
            <div className="total-row">
              <span>SGST {rate / 2}%:</span>
              <span className="amount">{formatAmount(amounts.sgst, currency)}</span>
            </div>
          </React.Fragment>
        ) : (
          <div key={String(rate)} className="total-row">
            <span>IGST {rate}%:</span>
            <span className="amount">{formatAmount(amounts.igst, currency)}</span>
          </div>
        ),
      )}
    </>
  )
}

interface RoundOffRowProps {
  isRoundOffEnabled: boolean
  roundOffAmount: number
  currency: string
  onToggle: (enabled: boolean) => void
}

function RoundOffRow({ isRoundOffEnabled, roundOffAmount, currency, onToggle }: RoundOffRowProps): JSX.Element {
  const roundOffDisplay = (() => {
    if (!isRoundOffEnabled || roundOffAmount === 0) { return '—' }
    const sign = roundOffAmount > 0 ? '+' : ''
    return sign + formatAmount(roundOffAmount, currency)
  })()

  return (
    <div className="total-row" style={{ alignItems: 'center' }}>
      <span className="flex items-center el-gap-field">
        Round Off:
        <Toggle checked={isRoundOffEnabled} size="sm" onChange={onToggle} />
      </span>
      <span className="amount">{roundOffDisplay}</span>
    </div>
  )
}

interface CreditLimitWarningProps {
  message: string
}

function CreditLimitWarning({ message }: CreditLimitWarningProps): JSX.Element {
  return (
    <div className="card el-p-md flex items-start gap-2 border border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20">
      <span className="text-yellow-600 dark:text-yellow-400 text-base leading-none mt-0.5">
        &#9888;
      </span>
      <div>
        <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
          Credit Limit Exceeded
        </span>
        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">{message}</p>
      </div>
    </div>
  )
}
